import { View, Text, Pressable, ScrollView, ActivityIndicator } from 'react-native'
import React, { useState } from 'react'
import { StyleSheet } from 'react-native-unistyles'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Clipboard from '@react-native-clipboard/clipboard'
import Toast from 'react-native-toast-message'
import Unicon from '@/components/General/Unicon'
import Chip from '@/components/General/Chip'
import SolidButton from '@/components/General/SolidButton'
import SheetModal from '@/components/General/SheetModal'
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder'
import Input from '@/components/General/inputs/Input'
import Toggle from '@/components/General/inputs/Toggle'
import { Query } from '@/utils/react-query/Queries'
import { QueryKeys } from '@/utils/react-query/QueryKeys'
import { createApiKey, revokeApiKey } from '@/data/apiKeys'
import { showAlert } from '@/utils/showAlert'
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements'
import { isFreeOrg } from '@/utils/organizationEntitlements'
import PlanRestrictedScreen from '@/components/General/PlanRestrictedScreen'

const SCOPE_RESOURCES = [
    { key: 'people', label: 'People', read: 'read:people', write: 'write:people' },
    { key: 'addresses', label: 'Addresses', read: 'read:addresses', write: 'write:addresses' },
    { key: 'smallgroups', label: 'Small Groups', read: 'read:smallgroups', write: 'write:smallgroups' },
];

const KNOWN_SCOPES = SCOPE_RESOURCES.flatMap(r => [r.read, r.write]);

const EXPIRATION_OPTIONS = [
    { key: 'never', label: 'Never', days: null },
    { key: '30', label: '30 days', days: 30 },
    { key: '90', label: '90 days', days: 90 },
    { key: '365', label: '1 year', days: 365 },
];

function formatDate(dateString) {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function isExpired(key) {
    return !!key.expiresAt && new Date(key.expiresAt) < new Date();
}

// ["read:people","write:people"] → "people: read+write"
function formatScopeLabels(scopes = []) {
    const labels = SCOPE_RESOURCES.map(r => {
        const hasRead = scopes.includes(r.read);
        const hasWrite = scopes.includes(r.write);
        if (!hasRead && !hasWrite) return null;
        return `${r.key}: ${hasRead && hasWrite ? 'read+write' : hasRead ? 'read' : 'write'}`;
    }).filter(Boolean);

    const extra = scopes.filter(s => !KNOWN_SCOPES.includes(s));
    return [...labels, ...extra];
}

function MetaItem({ label, value }) {
    return (
        <View style={styles.metaItem}>
            <Text style={styles.metaLabel}>{label}</Text>
            <Text style={styles.metaValue}>{value}</Text>
        </View>
    );
}

function ApiKeyCard({ apiKey, onRevoke }) {
    const revoked = !!apiKey.revokedAt;
    const expired = isExpired(apiKey);
    const scopeLabels = formatScopeLabels(apiKey.scopes);

    return (
        <View style={[styles.keyCard, revoked && styles.keyCardRevoked]} testID={`api-key-card-${apiKey.id}`}>
            <View style={styles.keyHeader}>
                <View style={styles.keyIconContainer}>
                    <Unicon name="key-outline" colorVariant="onPrimary" size={18} />
                </View>
                <View style={styles.keyTitleContainer}>
                    <Text style={[styles.keyName, revoked && styles.revokedText]} numberOfLines={1}>{apiKey.name}</Text>
                    <Text style={styles.keyPrefix}>{apiKey.prefix}…</Text>
                </View>
                {revoked && <Chip colorVariant="error" icon="ban-outline">Revoked</Chip>}
                {!revoked && expired && <Chip colorVariant="warning" icon="time-outline">Expired</Chip>}
            </View>

            {scopeLabels.length > 0 && (
                <View style={styles.scopeRow}>
                    {scopeLabels.map(label => (
                        <Chip key={label} colorVariant="primary">{label}</Chip>
                    ))}
                </View>
            )}

            <View style={styles.metaRow}>
                <MetaItem label="Created" value={formatDate(apiKey.createdAt)} />
                <MetaItem label="Last used" value={apiKey.lastUsedAt ? formatDate(apiKey.lastUsedAt) : 'Never'} />
                <MetaItem label="Expires" value={apiKey.expiresAt ? formatDate(apiKey.expiresAt) : 'Never'} />
            </View>

            {!revoked && (
                <View style={styles.keyActions}>
                    <SolidButton
                        testID={`revoke-key-button-${apiKey.id}`}
                        text="Revoke"
                        type="error"
                        variant="outlined"
                        size="sm"
                        iconName="trash-outline"
                        iconBefore
                        onPress={() => onRevoke(apiKey)}
                    />
                </View>
            )}
        </View>
    );
}

function ScopeResourceToggles({ resource, scopes, onToggle }) {
    return (
        <View style={styles.scopeResource}>
            <Text style={styles.scopeResourceLabel}>{resource.label}</Text>
            <View style={styles.scopeTogglesRow}>
                <View style={styles.scopeToggle}>
                    <Text style={styles.scopeToggleLabel}>Read</Text>
                    <Toggle
                        testID={`scope-toggle-${resource.key}-read`}
                        value={scopes.includes(resource.read)}
                        onValueChange={(on) => onToggle(resource.read, on)}
                    />
                </View>
                <View style={styles.scopeToggle}>
                    <Text style={styles.scopeToggleLabel}>Write</Text>
                    <Toggle
                        testID={`scope-toggle-${resource.key}-write`}
                        value={scopes.includes(resource.write)}
                        onValueChange={(on) => onToggle(resource.write, on)}
                    />
                </View>
            </View>
        </View>
    );
}

export default function ApiKeysScreen() {
    const queryClient = useQueryClient();
    const { data: loggedInUser, isLoading: userLoading } = Query.people.loggedInUser();
    const isAdmin = !!loggedInUser?.isAdmin;
    // API access is a paid-only module — never query keys on Free.
    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);

    const { data: keys, isLoading } = Query.apiKeys.all({ options: { enabled: isAdmin && !isFree } });

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [createdKey, setCreatedKey] = useState(null);
    const [name, setName] = useState('');
    const [scopes, setScopes] = useState([]);
    const [expiration, setExpiration] = useState('never');
    const [showRevoked, setShowRevoked] = useState(false);

    const createMutation = useMutation({
        mutationFn: createApiKey,
        onSuccess: (data) => {
            setCreatedKey(data);
            queryClient.invalidateQueries({ queryKey: QueryKeys.apiKeys.all() });
            Toast.show({ type: 'success', text2: 'API key created' });
        },
    });

    const revokeMutation = useMutation({
        mutationFn: revokeApiKey,
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'API key revoked' });
            queryClient.invalidateQueries({ queryKey: QueryKeys.apiKeys.all() });
        },
    });

    const resetCreateForm = () => {
        setName('');
        setScopes([]);
        setExpiration('never');
        setCreatedKey(null);
    };

    const openCreateModal = () => {
        // Reset on open (not close) so the raw-key reveal doesn't visually
        // swap back to the form during the sheet's exit animation.
        resetCreateForm();
        setIsCreateOpen(true);
    };

    const closeCreateModal = () => {
        setIsCreateOpen(false);
    };

    const toggleScope = (scope, on) => {
        setScopes(prev => on ? [...prev, scope] : prev.filter(s => s !== scope));
    };

    const handleCreate = () => {
        if (!name.trim()) {
            Toast.show({ type: 'error', text2: 'Enter a name for the API key' });
            return;
        }
        if (scopes.length === 0) {
            Toast.show({ type: 'error', text2: 'Select at least one scope' });
            return;
        }
        const days = EXPIRATION_OPTIONS.find(o => o.key === expiration)?.days;
        createMutation.mutate({
            name: name.trim(),
            scopes,
            expiresAt: days ? new Date(Date.now() + days * 86400000).toISOString() : null,
        });
    };

    const handleRevoke = (apiKey) => {
        showAlert({
            title: 'Revoke API Key',
            subtitle: `Are you sure you want to revoke "${apiKey.name}"? Any integrations using this key will immediately lose access. This cannot be undone.`,
            isDestructive: true,
            confirmText: 'Revoke Key',
            onConfirm: () => revokeMutation.mutate({ id: apiKey.id }),
        });
    };

    const handleCopy = () => {
        Clipboard.setString(createdKey?.rawKey ?? '');
        Toast.show({ type: 'success', text2: 'API key copied to clipboard' });
    };

    if (isFree) return <PlanRestrictedScreen />;

    if (userLoading) {
        return (
            <View style={styles.centerContainer}>
                <ActivityIndicator />
            </View>
        );
    }

    if (!isAdmin) {
        return (
            <View style={styles.centerContainer} testID="api-keys-requires-admin">
                <Unicon name="lock-closed-outline" size={40} colorVariant="textSecondary" />
                <Text style={styles.noAccessText}>This screen requires admin access.</Text>
            </View>
        );
    }

    const allKeys = keys ?? [];
    const activeKeys = allKeys.filter(k => !k.revokedAt);
    const revokedKeys = allKeys.filter(k => k.revokedAt);

    return (
        <View style={styles.container}>
            <ScrollView contentContainerStyle={styles.contentContainer} keyboardShouldPersistTaps="handled">
                <View style={styles.headerRow}>
                    <View style={styles.headerText}>
                        <Text style={styles.headerDescription}>
                            API keys let external tools access your organization&apos;s data with limited permissions.
                        </Text>
                    </View>
                    <SolidButton
                        testID="create-api-key-button"
                        text="New Key"
                        iconName="add"
                        iconBefore
                        size="md"
                        onPress={openCreateModal}
                    />
                </View>

                {isLoading && (
                    <View style={styles.loadingContainer}>
                        <ActivityIndicator />
                    </View>
                )}

                {!isLoading && activeKeys.length === 0 && revokedKeys.length === 0 && (
                    <EmptyListPlaceholder
                        message="No API keys yet. Create one to give an integration access to your data."
                        spacing={60}
                    />
                )}

                {!isLoading && activeKeys.length === 0 && revokedKeys.length > 0 && (
                    <EmptyListPlaceholder
                        message="No active API keys."
                        spacing={40}
                    />
                )}

                {activeKeys.map(apiKey => (
                    <ApiKeyCard key={apiKey.id} apiKey={apiKey} onRevoke={handleRevoke} />
                ))}

                {revokedKeys.length > 0 && (
                    <View style={styles.revokedSection}>
                        <Pressable
                            testID="show-revoked-toggle"
                            style={styles.revokedToggle}
                            onPress={() => setShowRevoked(prev => !prev)}
                        >
                            <Unicon
                                name={showRevoked ? 'chevron-down' : 'chevron-forward'}
                                size={16}
                                colorVariant="textSecondary"
                            />
                            <Text style={styles.revokedToggleText}>
                                {showRevoked ? 'Hide' : 'Show'} revoked keys ({revokedKeys.length})
                            </Text>
                        </Pressable>
                        {showRevoked && revokedKeys.map(apiKey => (
                            <ApiKeyCard key={apiKey.id} apiKey={apiKey} onRevoke={handleRevoke} />
                        ))}
                    </View>
                )}
            </ScrollView>

            <SheetModal
                isOpen={isCreateOpen}
                title={createdKey ? 'Save Your API Key' : 'Create API Key'}
                subtitle={createdKey ? createdKey.key?.name : undefined}
                cancelText={createdKey ? 'Done' : 'Cancel'}
                confirmText={createdKey ? undefined : 'Create Key'}
                onConfirm={createdKey ? undefined : handleCreate}
                onCancel={closeCreateModal}
                onClose={closeCreateModal}
                loading={createMutation.isPending}
            >
                {createdKey ? (
                    <View style={styles.revealContainer}>
                        <View style={styles.warningBanner} testID="api-key-warning">
                            <Unicon name="warning-outline" size={18} colorVariant="warning" />
                            <Text style={styles.warningText}>
                                This is the only time your API key will be shown. Copy it now and store it somewhere safe — you will not be able to see it again.
                            </Text>
                        </View>
                        <View style={styles.rawKeyBox}>
                            <Text style={styles.rawKeyText} selectable testID="raw-api-key-value">
                                {createdKey.rawKey}
                            </Text>
                        </View>
                        <View style={styles.copyButtonContainer}>
                            <SolidButton
                                testID="copy-api-key-button"
                                text="Copy Key"
                                iconName="copy-outline"
                                iconBefore
                                onPress={handleCopy}
                            />
                        </View>
                    </View>
                ) : (
                    <View style={styles.formContainer}>
                        <Input
                            title="Name"
                            placeholder="e.g. Donation kiosk sync"
                            value={name}
                            onUpdate={setName}
                            required
                            description="A label to help you remember what this key is for."
                        />

                        <Text style={styles.sectionLabel}>Scopes</Text>
                        <Text style={styles.sectionHint}>Choose what this key can read or change. At least one is required.</Text>
                        <View style={styles.scopeList} testID="scope-selector">
                            {SCOPE_RESOURCES.map(resource => (
                                <ScopeResourceToggles
                                    key={resource.key}
                                    resource={resource}
                                    scopes={scopes}
                                    onToggle={toggleScope}
                                />
                            ))}
                        </View>

                        <Text style={styles.sectionLabel}>Expiration</Text>
                        <View style={styles.expirationRow}>
                            {EXPIRATION_OPTIONS.map(option => (
                                <Pressable
                                    key={option.key}
                                    testID={`expiration-option-${option.key}`}
                                    style={[styles.expirationChip, expiration === option.key && styles.expirationChipSelected]}
                                    onPress={() => setExpiration(option.key)}
                                >
                                    <Text style={[styles.expirationChipText, expiration === option.key && styles.expirationChipTextSelected]}>
                                        {option.label}
                                    </Text>
                                </Pressable>
                            ))}
                        </View>
                    </View>
                )}
            </SheetModal>
        </View>
    )
}

const styles = StyleSheet.create(({ colors, fonts, shadows, spacing, typography, roundness }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    contentContainer: {
        padding: 20,
        gap: 16,
        _web: {
            maxWidth: 900,
            alignSelf: 'center',
            width: '100%',
        }
    },
    centerContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background,
        gap: 12,
        padding: 20,
    },
    noAccessText: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
    },
    headerText: {
        flex: 1,
    },
    headerDescription: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        lineHeight: 20,
    },
    loadingContainer: {
        paddingVertical: 40,
        alignItems: 'center',
    },
    keyCard: {
        borderRadius: 12,
        padding: 16,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        gap: 12,
    },
    keyCardRevoked: {
        opacity: 0.55,
    },
    keyHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    keyIconContainer: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    keyTitleContainer: {
        flex: 1,
        gap: 2,
    },
    keyName: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    revokedText: {
        textDecorationLine: 'line-through',
    },
    keyPrefix: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    scopeRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
    },
    metaRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 20,
    },
    metaItem: {
        gap: 2,
    },
    metaLabel: {
        fontSize: 11,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
    },
    metaValue: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    keyActions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
    revokedSection: {
        gap: 12,
    },
    revokedToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 4,
        _web: {
            cursor: 'pointer',
        }
    },
    revokedToggleText: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
    },
    formContainer: {
        paddingBottom: 20,
    },
    sectionLabel: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        marginTop: spacing.md,
        marginBottom: 4,
    },
    sectionHint: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textTertiary,
        marginBottom: 8,
    },
    scopeList: {
        gap: 8,
    },
    scopeResource: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: colors.surface,
        borderRadius: 10,
        paddingVertical: 10,
        paddingHorizontal: 16,
        gap: 12,
    },
    scopeResourceLabel: {
        fontSize: typography.body,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    scopeTogglesRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 20,
    },
    scopeToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    scopeToggleLabel: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    expirationRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    expirationChip: {
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: roundness.md,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        _web: {
            cursor: 'pointer',
        }
    },
    expirationChipSelected: {
        backgroundColor: colors.primaryContainer,
        borderColor: colors.primary,
    },
    expirationChipText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    expirationChipTextSelected: {
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    revealContainer: {
        gap: 16,
        paddingBottom: 20,
    },
    warningBanner: {
        flexDirection: 'row',
        gap: 10,
        alignItems: 'flex-start',
        backgroundColor: colors.warningContainer,
        borderRadius: 10,
        padding: 14,
    },
    warningText: {
        flex: 1,
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.text,
        lineHeight: 19,
    },
    rawKeyBox: {
        backgroundColor: colors.inputBackground,
        borderRadius: 10,
        padding: 16,
    },
    rawKeyText: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.text,
        letterSpacing: 0.3,
    },
    copyButtonContainer: {
        alignItems: 'center',
    },
}))
